import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSelector } from 'react-redux';

import LinearGradient from '../LinearGradient';
import { BookOpen, ChevronLeft, ChevronRight } from '../Icons';
import { useRemScale } from '../../lib/remScale';

const AUTO_COLLAPSE_MS = 8000;

const UnitHeaderOverlay = ({
  courseTitle = '',
  unitNumber,
  title = '',
  currentLessonName = '',
  completedLessons = 0,
  totalLessons = 0,
  top,
  rightBoundary,
}) => {
  const { rem } = useRemScale();
  const { width } = useWindowDimensions();
  const lessonStarted = useSelector(state => state.meeting.meeting.lessonStarted);
  const readyToMove = useSelector(state => state.meeting.meeting.ready_to_move);
  const rpmReady = useSelector(state => state.meeting.meeting.rpmReady);
  const metaverseReady = !!readyToMove && !!rpmReady;

  const [isOpen, setIsOpen] = useState(true);
  const [expandedLayout, setExpandedLayout] = useState({ width: 0, height: 0 });
  const openProgress = useRef(new Animated.Value(1)).current;
  const collapseTimerRef = useRef(null);
  const prevLessonStartedRef = useRef(lessonStarted);
  const prevMetaverseReadyRef = useRef(metaverseReady);

  // Keep the unit banner from running into the centered top HUD on smaller widths.
  // `rightBoundary` is the measured left edge of the top HUD center group.
  const viewportMaxWidth = width - rem(24);
  const fallbackMaxWidth = Math.max(rem(260), Math.min(viewportMaxWidth, width * 0.35));
  const measuredBoundary = Number(rightBoundary);
  const boundaryLimitedWidth = Number.isFinite(measuredBoundary)
    ? Math.max(rem(150), Math.min(viewportMaxWidth, measuredBoundary - rem(14)))
    : fallbackMaxWidth;
  const maxWidth = Math.min(fallbackMaxWidth, boundaryLimitedWidth);
  const hiddenX = -(expandedLayout.width || maxWidth) - rem(14);
  // Match TopHudBar's top inset so the chevron/panel aligns with its top edge.
  const topOffset = typeof top === 'number' ? top : rem(12);
  const collapsedHandleHeight = expandedLayout.height || rem(74);

  const completedSafe = Math.max(0, Number(completedLessons) || 0);
  const totalSafe = Math.max(0, Number(totalLessons) || 0);
  const progressPct =
    totalSafe > 0
      ? Math.round((Math.min(completedSafe, totalSafe) / totalSafe) * 100)
      : 0;
  const progressLabelColor = progressPct >= 50 ? '#0b1015' : '#FFFFFF';
  const highlightBorderColor = 'rgba(197,232,54,0.30)';

  const webGlassStyle =
    Platform.OS === 'web'
      ? {
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
        }
      : null;

  const translateX = openProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [hiddenX, 0],
  });
  const panelScale = openProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0.985, 1],
  });
  const collapsedOpacity = openProgress.interpolate({
    inputRange: [0, 0.2, 0.55, 1],
    outputRange: [1, 1, 0.08, 0],
  });
  const collapsedTranslateX = openProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -rem(10)],
  });
  const collapsedScale = openProgress.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 0.96],
  });

  useEffect(() => {
    const easing = isOpen
      ? Easing.out(Easing.cubic)
      : Easing.bezier(0.22, 1, 0.36, 1);

    Animated.timing(openProgress, {
      toValue: isOpen ? 1 : 0,
      duration: isOpen ? 280 : 340,
      easing,
      useNativeDriver: true,
    }).start();
  }, [isOpen, openProgress]);

  // Only auto-collapse when lesson transitions from not started -> started.
  // This prevents stale Redux values from closing the panel at initial load.
  useEffect(() => {
    if (!prevLessonStartedRef.current && lessonStarted && isOpen) {
      setIsOpen(false);
    }
    prevLessonStartedRef.current = lessonStarted;
  }, [lessonStarted, isOpen]);

  // Re-open whenever metaverse finishes loading.
  useEffect(() => {
    if (!prevMetaverseReadyRef.current && metaverseReady) {
      setIsOpen(true);
    }
    prevMetaverseReadyRef.current = metaverseReady;
  }, [metaverseReady]);

  useEffect(() => {
    if (!isOpen) {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
      return;
    }

    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
    }
    collapseTimerRef.current = setTimeout(() => {
      setIsOpen(false);
    }, AUTO_COLLAPSE_MS);

    return () => {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
    };
  }, [isOpen]);

  return (
    <>
      <Animated.View
        pointerEvents="box-none"
        style={{
          position: 'absolute',
          left: 0,
          top: topOffset,
          zIndex: 31,
          transform: [{ translateX }, { scale: panelScale }],
        }}
      >
        <TouchableOpacity
          activeOpacity={0.92}
          onPress={() => {
            if (isOpen) {
              setIsOpen(false);
            }
          }}
          onLayout={e => {
            const { width: measuredW, height: measuredH } =
              e?.nativeEvent?.layout || {};
            if (!measuredW || !measuredH) return;

            setExpandedLayout(prev => {
              if (
                Math.abs((prev.width || 0) - measuredW) < 1 &&
                Math.abs((prev.height || 0) - measuredH) < 1
              ) {
                return prev;
              }
              return { width: measuredW, height: measuredH };
            });
          }}
          style={[
            styles.panel,
            webGlassStyle,
            {
              borderColor: highlightBorderColor,
              maxWidth,
              borderTopRightRadius: rem(16),
              borderBottomRightRadius: rem(16),
              paddingHorizontal: rem(12),
              paddingVertical: rem(10),
            },
          ]}
        >
          <LinearGradient
            colors={['#C5E836', '#84cc16']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={[
              styles.iconWrap,
              {
                width: rem(36),
                height: rem(36),
                borderRadius: rem(9),
                marginRight: rem(10),
              },
            ]}
          >
            <BookOpen size={rem(18)} color="#0b1015" strokeWidth={2.5} />
          </LinearGradient>

          <View style={{ flex: 1, minWidth: 0 }}>
            {!!courseTitle && (
              <Text
                style={[
                  styles.courseText,
                  {
                    fontSize: rem(12),
                    marginBottom: rem(3),
                  },
                ]}
                numberOfLines={2}
                ellipsizeMode="tail"
              >
                {`Course - ${courseTitle}`}
              </Text>
            )}

            <Text
              style={[
                styles.unitText,
                {
                  fontSize: rem(14),
                  lineHeight: rem(18),
                },
              ]}
              numberOfLines={3}
              ellipsizeMode="tail"
            >
              {typeof unitNumber === 'number'
                ? `Unit ${unitNumber}: ${title || 'Untitled Unit'}`
                : `Unit: ${title || 'Untitled Unit'}`}
            </Text>

            <Text
              style={[
                styles.lessonText,
                {
                  fontSize: rem(12),
                  marginTop: rem(4),
                },
              ]}
              numberOfLines={2}
              ellipsizeMode="tail"
            >
              {`Lesson: ${currentLessonName || 'Select a lesson to begin'}`}
            </Text>

            <View
              style={{
                marginTop: rem(7),
                height: rem(20),
                borderRadius: rem(8),
                backgroundColor: 'rgba(255,255,255,0.16)',
                overflow: 'hidden',
                justifyContent: 'center',
              }}
            >
              <LinearGradient
                colors={['#fbbf24', '#fde68a', '#fbbf24']}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${progressPct}%`,
                }}
              />
              <View style={styles.progressTextWrap}>
                <Text
                  style={{
                    color: progressLabelColor,
                    fontSize: rem(11),
                    fontWeight: '700',
                  }}
                >
                  {`${Math.min(completedSafe, totalSafe)}/${totalSafe} lessons complete`}
                </Text>
              </View>
            </View>
          </View>

          <View
            style={{
              marginLeft: rem(10),
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ChevronLeft size={rem(16)} color="rgba(197,232,54,0.9)" strokeWidth={2.4} />
          </View>
        </TouchableOpacity>
      </Animated.View>

      <Animated.View
        pointerEvents={isOpen ? 'none' : 'auto'}
        style={[
          styles.collapsedHandle,
          webGlassStyle,
          {
            borderColor: highlightBorderColor,
            opacity: collapsedOpacity,
            transform: [{ translateX: collapsedTranslateX }, { scale: collapsedScale }],
            top: topOffset,
            width: rem(42),
            height: collapsedHandleHeight,
            borderTopRightRadius: rem(16),
            borderBottomRightRadius: rem(16),
          },
        ]}
      >
        <TouchableOpacity
          activeOpacity={0.9}
          disabled={isOpen}
          onPress={() => setIsOpen(true)}
          style={[
            styles.collapsedTouchable,
            {
              borderTopRightRadius: rem(16),
              borderBottomRightRadius: rem(16),
            },
          ]}
        >
          <ChevronRight size={rem(20)} color="#C5E836" strokeWidth={2.6} />
        </TouchableOpacity>
      </Animated.View>
    </>
  );
};

const styles = StyleSheet.create({
  panel: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: 'rgba(15,16,20,0.84)',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderColor: 'rgba(197,232,54,0.30)',
    shadowColor: '#000',
    shadowOpacity: 0.4,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
    zIndex: 31,
  },
  iconWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
  },
  courseText: {
    color: 'rgba(197,232,54,0.9)',
    fontWeight: '700',
    letterSpacing: 0.25,
    textTransform: 'uppercase',
  },
  unitText: {
    color: '#FFFFFF',
    fontWeight: '800',
    letterSpacing: 0.2,
  },
  lessonText: {
    color: 'rgba(255,255,255,0.78)',
    fontWeight: '600',
  },
  progressTextWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  collapsedHandle: {
    position: 'absolute',
    left: 0,
    zIndex: 32,
    backgroundColor: 'rgba(15,16,20,0.90)',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderColor: 'rgba(197,232,54,0.30)',
    shadowColor: '#000',
    shadowOpacity: 0.35,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
  },
  collapsedTouchable: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default UnitHeaderOverlay;
